import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Reservation, Shift, ReservationsCsvExportParams , ReservationsExportFieldSettings, DelimiterOption } from '@model/reservation';
import {ReservationsExportService} from '../lib/reservations-export.service';

type ExportFieldKey = Exclude<keyof ReservationsExportFieldSettings, 'delimiter'>;

interface ExportFieldOption {
  key: ExportFieldKey;
  labelKey: string;
}

@Component({
  selector: 'app-reservations-export',
  templateUrl: './reservations-export.component.html',
  styleUrls: ['./reservations-export.component.scss']
})
export class ReservationsExportComponent implements OnChanges {

  constructor(
    private reservationsExportService: ReservationsExportService
  ) { }

  @Input() reservations: Reservation[] = [];
  @Input() selectedShift: Shift | null = null;
  @Input() selectedShiftTitle: string | null = null;
  @Input() selectedDate: Date | string | null = null;
  @Input() restaurantTitle: string | null = null;

  @Output() hide = new EventEmitter<void>();

   

  exportFormat: 'csv' | 'excel' = 'excel';
  settings: ReservationsExportFieldSettings = this.reservationsExportService.getDefaultFieldSettings();
  message: string | null = null;

  readonly fieldOptions: ExportFieldOption[] = [
    { key: 'status', labelKey: 'reservationsExport.fields.status' },
    { key: 'purpose', labelKey: 'reservationsExport.fields.purpose' },
    { key: 'arrivalDate', labelKey: 'reservationsExport.fields.arrivalDate' },
    { key: 'arrivalTime', labelKey: 'reservationsExport.fields.arrivalTime' },
    { key: 'fullname', labelKey: 'reservationsExport.fields.fullname' },
    { key: 'covers', labelKey: 'reservationsExport.fields.covers' },
    { key: 'type', labelKey: 'reservationsExport.fields.type' },
    { key: 'telephone', labelKey: 'reservationsExport.fields.telephone' },
    { key: 'allergies', labelKey: 'reservationsExport.fields.allergies' },
    { key: 'reservationTags', labelKey: 'reservationsExport.fields.reservationTags' },
    { key: 'customerTags', labelKey: 'reservationsExport.fields.customerTags' },
    { key: 'discount', labelKey: 'reservationsExport.fields.discount' },
    { key: 'cake', labelKey: 'reservationsExport.fields.cake' },
    { key: 'fixMenu', labelKey: 'reservationsExport.fields.fixMenu' },
    { key: 'smokingArea', labelKey: 'reservationsExport.fields.smokingArea' },
    { key: 'email', labelKey: 'reservationsExport.fields.email' },
    { key: 'category', labelKey: 'reservationsExport.fields.category' },
    { key: 'reservationId', labelKey: 'reservationsExport.fields.reservationId' },
    { key: 'tables', labelKey: 'reservationsExport.fields.tables' },
    { key: 'duration', labelKey: 'reservationsExport.fields.duration' },
    { key: 'returnTime', labelKey: 'reservationsExport.fields.returnTime' },
    { key: 'createdBy', labelKey: 'reservationsExport.fields.createdBy' },
    { key: 'confirmedBy', labelKey: 'reservationsExport.fields.confirmedBy' },
    { key: 'notes', labelKey: 'reservationsExport.fields.notes' },
    { key: 'source', labelKey: 'reservationsExport.fields.source' },
    { key: 'sourceId', labelKey: 'reservationsExport.fields.sourceId' },
    { key: 'customerId', labelKey: 'reservationsExport.fields.customerId' },
    { key: 'bill', labelKey: 'reservationsExport.fields.bill' },
    { key: 'restaurantName', labelKey: 'reservationsExport.fields.restaurantName' },
    { key: 'creationTime', labelKey: 'reservationsExport.fields.creationTime' },
    { key: 'timePerformed', labelKey: 'reservationsExport.fields.timePerformed' },
    { key: 'metadata', labelKey: 'reservationsExport.fields.metadata' }
  ];

  delimiterOptions: DelimiterOption[] = [
  { value: ',', display: ',' },
  { value: ';', display: ';' }
];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reservations']) {
      this.message = null;
    }
  }

    get hasReservations(): boolean {
        return (this.reservations?.length ?? 0) > 0;
    }

    get selectedFieldsCount(): number {
        return this.fieldOptions.filter(field => this.settings[field.key] === true).length;
    }

    get allFieldsSelected(): boolean {
        return this.fieldOptions.every(field => this.settings[field.key] === true);
    }

    get isCsvSelected(): boolean {
        return this.exportFormat === 'csv';
    }

  selectAllFields(): void {
    for (const field of this.fieldOptions) {
      this.settings[field.key] = true;
    }
  }

  clearAllFields(): void {
    for (const field of this.fieldOptions) {
      this.settings[field.key] = false;
    }
  }

  toggleField(field: ExportFieldKey): void {
    this.settings[field] = !this.settings[field];
  }

  toggleAllFields(checked: boolean): void {
  if (checked) {
    this.selectAllFields();
  } else {
    this.clearAllFields();
  }
}

  cancel(): void {
    this.hide.emit();
  }

  export(): void {
    this.message = null;

      

    if (!this.hasReservations) {
      this.message = 'reservationsExport.validation.noReservations';
      return;
    }

    if (this.selectedFieldsCount <= 0) {
      this.message = 'reservationsExport.validation.noFieldsSelected';
      return;
    }

    const params: ReservationsCsvExportParams = {
      reservations: this.reservations ?? [],
      settings: {
        ...this.settings
      },
      selectedShift: this.selectedShift,
      selectedShiftTitle: this.selectedShiftTitle,
      selectedDate: this.selectedDate,
      restaurantTitle: this.restaurantTitle
    };

      let success;

      if (this.exportFormat !== 'csv') {
          success = this.reservationsExportService.exportToExcel(params);
      } else {
          success = this.reservationsExportService.exportToCsv(params);
      }

     

    if (!success) {
      this.message = 'reservationsExport.validation.noReservations';
      return;
    }

    this.hide.emit();
  }
}